/* One unfinished groomsmen set in this tab; never stores cart or approval state. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(root);
  else root.JEGroomsmanDraft = factory(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';
  const VERSION = 1, KEY = 'je:groomsmen-draft:v1', TTL = 24 * 60 * 60 * 1000, MAX_BYTES = 32768;
  const WEDDING_STYLES = ['tux', 'side-monogram', 'split-monogram', 'divided-initials', 'bow-tie', 'mountain', 'vertical-name'];
  const GIFT_STYLES = Object.freeze(['adventure-badge', 'family-wreath', 'classic-initial', 'outline-bow-tie', 'circle-badge', 'golf-monogram', 'split-name-monogram', 'fishing-name', 'deer-name', 'mustache', 'flag-name', 'initial-role', 'dad-established', 'underlined-initial', 'right-side-monogram', 'antler-monogram', 'bow-tie-badge', 'framed-split-monogram', 'bridal-party', 'girls-trip', 'rustic-antler-monogram']);
  const BRIDAL_ARTWORKS = Object.freeze(['bridesmaid', 'bride', 'maid-of-honor', 'maid-of-honour', 'mother-of-the-bride', 'sister-of-the-bride', 'brides-bestie', 'bride-tribe', 'team-bride', 'bride-to-be', 'future-mrs']);
  const GIRLS_ARTWORKS = Object.freeze(['almost-married', 'blonde', 'boujee', 'crazy', 'drunk', 'flirty', 'funny', 'loud', 'old', 'party', 'pregnant', 'single', 'sober']);
  const ARTWORKS_BY_STYLE = Object.freeze({ 'bridal-party': BRIDAL_ARTWORKS, 'girls-trip': GIRLS_ARTWORKS, 'rustic-antler-monogram': Object.freeze(Array.from('abcdefghijklmnopqrstuvwxyz')) });
  const STYLES = Object.freeze([...WEDDING_STYLES, ...GIFT_STYLES]);
  const HANDLES = Object.freeze(STYLES.map(style => ['bridal-party', 'girls-trip', 'rustic-antler-monogram'].includes(style) ? style + '-brrware-can-cooler' : style === 'tux' ? 'personalized-groomsmen-brrware-can-cooler' : (GIFT_STYLES.includes(style) ? 'personalized-' : 'personalized-groomsmen-') + style + '-brrware-can-cooler'));
  const ROLE_CHOICES = Object.freeze(['Groomsman', 'Best Man', 'Groom', 'Officiant', 'custom']);
  const ADJUSTMENTS = Object.freeze(['name', 'initials', 'role', 'date', 'icon']);
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = (value, max) => typeof value === 'string' && value.length <= max ? value : null;
  function styleForHandle(handle) { const index = HANDLES.indexOf(handle); return index < 0 ? null : STYLES[index]; }
  function eligible(options) {
    const style = styleForHandle(options?.handle);
    return !!style && (!options.style || options.style === style) && !options.cartEditing && !options.pending;
  }
  function validISO(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  }
  function parseDate(value) {
    if (typeof value !== 'string') return '';
    const raw = value.trim();
    if (validISO(raw)) return raw;
    const parts = /^(\d{1,2})([./-])(\d{1,2})\2(\d{4})$/.exec(raw);
    if (!parts) return '';
    const iso = parts[4] + '-' + parts[1].padStart(2, '0') + '-' + parts[3].padStart(2, '0');
    return validISO(iso) ? iso : '';
  }
  function formatDate(iso) {
    if (!validISO(iso)) return '';
    const [year, month, day] = iso.split('-'); return Number(month) + '.' + Number(day) + '.' + year;
  }
  function initialsFor(row, style) {
    const count = style === 'divided-initials' ? 2 : ['side-monogram', 'split-monogram', 'classic-initial', 'split-name-monogram', 'initial-role', 'underlined-initial', 'right-side-monogram', 'antler-monogram', 'framed-split-monogram'].includes(style) ? 1 : 0;
    if (!count) return '';
    const explicit = row?.initialsByCount?.[count];
    if (typeof explicit === 'string' && explicit.trim()) return explicit;
    const words = String(row?.name || '').normalize('NFC').trim().split(/\s+/).map(word => word.match(/\p{L}[\p{M}]*|\p{N}/gu) || []).filter(word => word.length);
    if (!words.length) return '';
    const letters = count === 1 ? [words[0][0]] : words.length > 1 ? [words[0][0], words[words.length - 1][0]] : words[0].slice(0, 2);
    return letters.join('').toUpperCase();
  }
  function normalizeRow(row) {
    if (!object(row)) return null;
    const name = text(row.name, 80), roleChoice = text(row.roleChoice, 60), customRole = text(row.customRole, 60), color = text(row.color, 100);
    if (name === null || !ROLE_CHOICES.includes(roleChoice) || customRole === null || color === null) return null;
    if (row.followSetColor !== undefined && typeof row.followSetColor !== 'boolean') return null;
    const cache = row.initialsByCount ?? {};
    if (!object(cache)) return null;
    const one = text(cache[1] ?? '', 8), two = text(cache[2] ?? '', 8);
    if (one === null || two === null) return null;
    let dateText = text(row.dateText ?? '', 32);
    if (dateText === null) return null;
    // Text is authoritative. An old ISO date must not replace newly entered,
    // unparseable lettering; that lettering remains recoverable on other styles.
    if (!dateText && validISO(row.dateISO)) dateText = formatDate(row.dateISO);
    const dateISO = parseDate(dateText);
    let giftFields;
    if (row.giftFields !== undefined) {
      const value = row.giftFields;
      if (!object(value) || text(value.location ?? '', 60) === null || text(value.phrase ?? '', 80) === null || !object(value.titles ?? {})) return null;
      const titles = {};
      for (const style of ['family-wreath', 'dad-established']) {
        if (!own(value.titles ?? {}, style)) continue;
        if (text(value.titles[style], 24) === null) return null;
        titles[style] = value.titles[style];
      }
      if (value.quantity !== undefined && (!Number.isInteger(value.quantity) || value.quantity < 1 || value.quantity > 99)) return null;
      if (value.artwork !== undefined && !BRIDAL_ARTWORKS.includes(value.artwork)) return null;
      if (value.artworksByStyle !== undefined && !object(value.artworksByStyle)) return null;
      const artworksByStyle = value.artwork ? { 'bridal-party': value.artwork } : {};
      for (const [style, values] of Object.entries(ARTWORKS_BY_STYLE)) {
        if (!own(value.artworksByStyle || {}, style)) continue;
        if (!values.includes(value.artworksByStyle[style])) return null;
        artworksByStyle[style] = value.artworksByStyle[style];
      }
      giftFields = { ...(value.quantity ? { quantity: value.quantity } : {}), location: value.location || '', phrase: value.phrase || '', titles, ...(artworksByStyle['bridal-party'] ? { artwork: artworksByStyle['bridal-party'] } : {}), ...(Object.keys(artworksByStyle).length ? { artworksByStyle } : {}) };
    }
    return { name, roleChoice, customRole, dateText, dateISO, color, followSetColor: row.followSetColor, initialsByCount: { 1: one, 2: two }, ...(giftFields ? { giftFields } : {}) };
  }
  function normalizeAdjustments(source) {
    const result = {};
    if (!object(source)) return result;
    for (const style of STYLES) {
      if (!own(source, style) || !object(source[style])) continue;
      const values = {};
      for (const key of ADJUSTMENTS) {
        const value = source[style][key];
        if (typeof value === 'number' && Number.isFinite(value) && value >= .5 && value <= 1.5 && Math.abs(value * 20 - Math.round(value * 20)) < .0000001) values[key] = Math.round(value * 20) / 20;
      }
      if (Object.keys(values).length) result[style] = values;
    }
    return result;
  }
  function normalizeSnapshot(source) {
    if (!object(source) || !Array.isArray(source.rows) || source.rows.length > 24) return null;
    const rows = source.rows.map(normalizeRow);
    if (rows.some(row => row === null)) return null;
    const providedColor = text(source.setColor ?? '', 100); if (providedColor === null) return null;
    // Legacy drafts had only individual colors. Use their first color as the
    // default and retain every different color as an explicit exception.
    const setColor = providedColor || rows[0]?.color || '';
    for (const row of rows) {
      if (row.followSetColor === undefined) row.followSetColor = row.color === setColor;
      if (row.followSetColor && setColor) row.color = setColor;
    }
    let sharedDate;
    if (source.sharedDate === undefined) {
      // Older drafts stored dates per recipient. Only a uniform set can be
      // migrated automatically; different dates need an explicit choice.
      const dates = new Set(rows.map(row => row.dateText));
      const dateText = dates.size > 1 ? '' : rows[0]?.dateText || '';
      sharedDate = { dateText, dateISO: parseDate(dateText), mixed: dates.size > 1 };
    } else {
      const value = source.sharedDate;
      if (!object(value) || typeof value.mixed !== 'boolean' || text(value.dateText, 32) === null || text(value.dateISO ?? '', 10) === null) return null;
      const dateText = value.mixed ? '' : value.dateText || (validISO(value.dateISO) ? formatDate(value.dateISO) : '');
      sharedDate = { dateText, dateISO: parseDate(dateText), mixed: value.mixed };
    }
    if (!sharedDate.mixed) for (const row of rows) { row.dateText = sharedDate.dateText; row.dateISO = sharedDate.dateISO; }
    const activeIndex = Number.isInteger(source.activeIndex) && source.activeIndex >= 0 && source.activeIndex < rows.length ? source.activeIndex : 0;
    let sharedGiftFields;
    if (source.sharedGiftFields !== undefined) {
      if (!object(source.sharedGiftFields) || text(source.sharedGiftFields.year ?? '', 4) === null) return null;
      // Invalid or partial year input is recoverable draft text; review validates it.
      const presentation = source.sharedGiftFields.presentation;
      if (presentation !== undefined && !['text-only', 'text-silhouette'].includes(presentation)) return null;
      const orientation = source.sharedGiftFields.orientation;
      if (orientation !== undefined && !['original', 'vertical'].includes(orientation)) return null;
      sharedGiftFields = { year: source.sharedGiftFields.year || '', ...(presentation ? { presentation } : {}), ...(orientation ? { orientation } : {}) };
    }
    return { rows, activeIndex, setColor, sharedDate, adjustmentsByStyle: normalizeAdjustments(source.adjustmentsByStyle), ...(sharedGiftFields ? { sharedGiftFields } : {}) };
  }
  function create({ storage, now = () => Date.now() } = {}) {
    const getStorage = () => storage === undefined ? root.sessionStorage : storage;
    function remove() { try { getStorage()?.removeItem(KEY); } catch {} }
    function read(options) {
      if (!eligible(options)) return null;
      try {
        const raw = getStorage()?.getItem(KEY); if (!raw) return null;
        if (raw.length > MAX_BYTES) { remove(); return null; }
        const saved = JSON.parse(raw), time = now();
        if (!object(saved) || saved.kind !== 'groomsmen-draft' || saved.version !== VERSION || !styleForHandle(saved.sourceHandle) ||
            !Number.isFinite(saved.savedAt) || saved.savedAt > time + 60000 || time - saved.savedAt >= TTL) { remove(); return null; }
        const draft = normalizeSnapshot(saved);
        if (!draft) { remove(); return null; }
        const style = styleForHandle(options.handle);
        return { ...draft, version: VERSION, sourceHandle: saved.sourceHandle, savedAt: saved.savedAt,
          rows: draft.rows.map(row => ({ ...row, initials: initialsFor(row, style),
            date: style === 'tux' ? row.dateISO : row.dateText,
            unconvertedDateText: style === 'tux' && row.dateText && !row.dateISO ? row.dateText : '' })) };
      } catch { remove(); return null; }
    }
    function save(options) {
      if (!eligible(options)) return false;
      const draft = normalizeSnapshot(options); if (!draft) return false;
      const payload = JSON.stringify({ kind: 'groomsmen-draft', version: VERSION, sourceHandle: options.handle, savedAt: now(), ...draft });
      if (payload.length > MAX_BYTES) return false;
      try { const target = getStorage(); if (!target) return false; target.setItem(KEY, payload); return true; } catch { return false; }
    }
    function clear(options) { if (!eligible(options)) return false; remove(); return true; }
    return Object.freeze({ read, save, clear });
  }
  const browser = create();
  return Object.freeze({ VERSION, KEY, TTL, HANDLES, STYLES, GIFT_STYLES, styleForHandle, parseDate, formatDate, initialsFor, create, ...browser });
});
